import React, { useEffect, useRef, useState } from "react";
import { Bell, AlertTriangle, Info, AlertOctagon } from "lucide-react";
import { useNotifications, useMarkNotificationsRead } from "@/hooks/useNotifications";
import type { Notification, NotificationSeverity } from "@/api/notifications";

const sevIcon: Record<NotificationSeverity, React.ComponentType<{ size?: number; className?: string }>> = {
  info: Info,
  warn: AlertTriangle,
  error: AlertOctagon,
};

const sevClass: Record<NotificationSeverity, string> = {
  info: "text-info-text",
  warn: "text-warning-text",
  error: "text-danger-text",
};

function timeAgo(iso: string): string {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export function NotificationsMenu() {
  const { data } = useNotifications();
  const markRead = useMarkNotificationsRead();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const items: Notification[] = data?.notifications ?? [];
  const unread = data?.unread_count ?? 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        className="relative h-9 w-9 rounded-full flex items-center justify-center text-text hover:bg-brand/10 transition-colors cursor-pointer"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-[3px] rounded-full bg-danger-text text-white text-[10px] font-semibold flex items-center justify-center leading-none">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-xs w-96 max-w-[92vw] rounded-md border border-border bg-surface shadow-card overflow-hidden z-30">
          <div className="px-md py-sm border-b border-border flex items-center justify-between">
            <div className="text-sm font-semibold text-text">Notifications</div>
            <button
              type="button"
              onClick={() => markRead.mutate(null)}
              disabled={unread === 0 || markRead.isPending}
              className="text-xs text-brand hover:underline disabled:opacity-40 disabled:no-underline cursor-pointer"
            >
              Mark all read
            </button>
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <li className="px-md py-lg text-sm text-textLight text-center">
                Nothing new.
              </li>
            )}
            {items.map((n) => {
              const Icon = sevIcon[n.severity] ?? Info;
              return (
                <li
                  key={n.id}
                  className={`flex items-start gap-sm px-md py-sm border-b border-border last:border-b-0 ${
                    n.is_read ? "" : "bg-brand/5"
                  }`}
                >
                  <Icon size={16} className={`mt-[2px] shrink-0 ${sevClass[n.severity] ?? ""}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-xs">
                      <div className="text-sm font-medium text-text truncate">{n.title}</div>
                      <div className="text-[11px] text-textLight ml-auto shrink-0">{timeAgo(n.created_at)}</div>
                    </div>
                    {n.body && (
                      <div className="text-xs text-textLight mt-[2px] line-clamp-2">{n.body}</div>
                    )}
                  </div>
                  {!n.is_read && (
                    <button
                      type="button"
                      onClick={() => markRead.mutate([n.id])}
                      title="Mark read"
                      className="h-2 w-2 mt-[6px] rounded-full bg-brand shrink-0"
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
